import { apiRequest, API_BASE_URL } from './api';

// ── Types ────────────────────────────────────────────

export type ExpenseStatus = 'pending' | 'paid' | 'rejected' | 'void';
export type ExpensePaymentMethod = 'cash' | 'cheque' | 'bank_transfer';

export type ExpenseRecord = {
    id: string;
    organization_id: string;
    school_id: string;
    expense_category_id: string;
    vendor_id: string | null;
    title: string;
    description: string | null;
    amount: string;
    expense_date: string;
    status: ExpenseStatus;
    payment_method: ExpensePaymentMethod | null;
    reference_number: string | null;
    voucher_number: string | null;
    created_at: string;
    updated_at: string;
    category?: {
        id: string;
        name: string;
    };
    receipts?: {
        id: string;
        original_name: string;
        url: string;
    }[];
};

export type ExpenseListMeta = {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
};

export type ExpenseListResponse = {
    data: ExpenseRecord[];
    meta: ExpenseListMeta;
};

// ── API Functions ────────────────────────────────────

export async function fetchExpenses(params?: {
    expense_category_id?: string;
    status?: string;
    per_page?: number;
    page?: number;
}): Promise<ExpenseListResponse> {
    const query = new URLSearchParams();
    if (params?.expense_category_id) query.set('expense_category_id', params.expense_category_id);
    if (params?.status) query.set('status', params.status);
    if (params?.per_page) query.set('per_page', String(params.per_page));
    if (params?.page) query.set('page', String(params.page));
    const qs = query.toString();

    const res = await apiRequest<ExpenseRecord[]>(
        `/api/expenses${qs ? `?${qs}` : ''}`
    );

    return {
        data: res.data,
        meta: (res as unknown as { meta: ExpenseListMeta }).meta,
    };
}

export async function createExpense(payload: {
    school_id: string;
    expense_category_id: string;
    vendor_id?: string | null;
    title: string;
    description?: string | null;
    amount: number;
    expense_date: string;
    status?: ExpenseStatus;
    payment_method?: ExpensePaymentMethod | null;
    reference_number?: string | null;
    receipts?: File[];
}): Promise<ExpenseRecord> {
    const formData = new FormData();
    formData.append('school_id', payload.school_id);
    formData.append('expense_category_id', payload.expense_category_id);
    if (payload.vendor_id) formData.append('vendor_id', payload.vendor_id);
    formData.append('title', payload.title);
    if (payload.description) formData.append('description', payload.description);
    formData.append('amount', String(payload.amount));
    formData.append('expense_date', payload.expense_date);
    if (payload.status) formData.append('status', payload.status);
    if (payload.payment_method) formData.append('payment_method', payload.payment_method);
    if (payload.reference_number) formData.append('reference_number', payload.reference_number);
    if (payload.receipts) {
        payload.receipts.forEach((file) => {
            formData.append('receipts[]', file);
        });
    }

    const token = localStorage.getItem('auth_token');
    const headers: Record<string, string> = {
        'Accept': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
    };

    const response = await fetch(`${API_BASE_URL}/api/expenses`, {
        method: 'POST',
        headers,
        body: formData,
    });

    const json = await response.json();

    if (!response.ok || !json.success) {
        throw {
            success: false,
            message: json.message || `Request failed with status ${response.status}`,
            errors: json.errors,
        };
    }

    return json.data as ExpenseRecord;
}

export async function updateExpense(
    id: string,
    payload: {
        expense_category_id?: string;
        vendor_id?: string | null;
        title?: string;
        description?: string | null;
        amount?: number;
        expense_date?: string;
        status?: ExpenseStatus;
        payment_method?: ExpensePaymentMethod | null;
        reference_number?: string | null;
        receipts?: File[];
    }
): Promise<ExpenseRecord> {
    const formData = new FormData();
    formData.append('_method', 'PUT');
    if (payload.expense_category_id) formData.append('expense_category_id', payload.expense_category_id);
    if (payload.vendor_id) formData.append('vendor_id', payload.vendor_id);
    if (payload.title) formData.append('title', payload.title);
    if (payload.description !== undefined) formData.append('description', payload.description ?? '');
    if (payload.amount !== undefined) formData.append('amount', String(payload.amount));
    if (payload.expense_date) formData.append('expense_date', payload.expense_date);
    if (payload.status) formData.append('status', payload.status);
    if (payload.payment_method !== undefined) formData.append('payment_method', payload.payment_method ?? '');
    if (payload.reference_number !== undefined) formData.append('reference_number', payload.reference_number ?? '');
    if (payload.receipts) {
        payload.receipts.forEach((file) => {
            formData.append('receipts[]', file);
        });
    }

    const token = localStorage.getItem('auth_token');
    const headers: Record<string, string> = {
        'Accept': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
    };

    const response = await fetch(`${API_BASE_URL}/api/expenses/${id}`, {
        method: 'POST',
        headers,
        body: formData,
    });

    const json = await response.json();

    if (!response.ok || !json.success) {
        throw {
            success: false,
            message: json.message || `Request failed with status ${response.status}`,
            errors: json.errors,
        };
    }

    return json.data as ExpenseRecord;
}

export async function deleteExpense(id: string): Promise<void> {
    await apiRequest(`/api/expenses/${id}`, {
        method: 'DELETE',
    });
}
